import { Stack, Textarea, Button, HStack, Spacer } from '@chakra-ui/react'
import type { FormEvent } from 'react'
import { useState } from 'react'

import type { DirectionType } from '../aiNodeMenu'

import FormulaSuggestions from './formulaSuggestions'

import { toaster } from '@app/components/ui/toaster'
import { useStore } from '@app/store'
import type { DomainNode, DomainObject } from '@app/types'
import { MetricFormulasSuggest } from '@graphql/documents/ai_assistant.graphql'
import type { MetricFormulasSuggestMutation, MetricFormulasSuggestMutationVariables } from '@graphql/queries'

interface ExtractMetricsFromFormulaPanelProps {
  node: DomainNode
  domainObject: DomainObject
  aiAssistantSlug: string
  direction: DirectionType
  handle: 'top' | 'bottom' | 'left' | 'right'
  onSuccess?: () => void
  handleBack?: () => void
  setLoading: (loading: boolean) => void
  loading: boolean
}

const ExtractMetricsFromFormulaPanel = ({
  node,
  direction,
  handle,
  onSuccess,
  handleBack,
  setLoading,
  loading
}: ExtractMetricsFromFormulaPanelProps) => {
  const [additionalInstructions, setAdditionalInstructions] = useState('')
  const [suggestions, setSuggestions] = useState<string[]>([])
  const actionMutation = useStore.use.actionMutation()

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)
    setSuggestions([])
    try {
      const result = await actionMutation<MetricFormulasSuggestMutation, MetricFormulasSuggestMutationVariables>(
        MetricFormulasSuggest,
        { nodeId: node.nodeId, additionalInstructions }
      )
      if (result.error) {
        if (result.error.networkError) {
          toaster.create({ title: 'Error', description: result.error.networkError.message, type: 'error' })
        } else {
          const errors = result.error.graphQLErrors.map((err) => err.message).join(', ')
          toaster.create({ title: 'Error', description: errors, type: 'error' })
        }
      } else {
        const formulas = result.data?.metricFormulasSuggest?.result || []
        setSuggestions(formulas)
      }
    } catch (err) {
      toaster.create({ title: 'Error', description: err.message, type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Stack>
        <Textarea
          id="additionalInstructions"
          value={additionalInstructions}
          onChange={(e) => setAdditionalInstructions(e.target.value)}
          placeholder="Optional: Add any extra context or instructions for the AI or leave blank if you're feeling lucky."
          rows={4}
          disabled={loading}
        />

        <HStack>
          <Button variant="ghost" onClick={handleBack} size="2xs">
            ← Back
          </Button>
          <Spacer />
          <Button type="submit" colorScheme="purple" loading={loading} disabled={loading} size="xs">
            Suggest formulas
          </Button>
        </HStack>

        <FormulaSuggestions
          suggestions={suggestions}
          node={node}
          direction={direction}
          handle={handle}
          additionalInstructions={additionalInstructions}
          onSuccess={onSuccess}
          setLoading={setLoading}
          loading={loading}
        />
      </Stack>
    </form>
  )
}

export default ExtractMetricsFromFormulaPanel
